import type { ComponentContent, PageContent } from '~/types/cms'
import type { MaybeRef } from 'vue'
import { mixed, object, string } from 'yup'
import { SessionStore } from '~/stores/session'

export default class AccountValidation {
  static use(content?: MaybeRef<Pick<PageContent | ComponentContent, 'ext'>>, requireName = true) {
    const session = SessionStore.use()

    const usedContent = unref(content)

    const defaultMessages = {
      invalid_first: 'Please enter a valid first name',
      invalid_last: 'Please enter a valid last name',
      invalid_phone: 'Please enter a valid phone number',
      invalid_email: 'Please enter a valid email address'
    }

    const signInValidation = object({
      'account-identifier': mixed().test({
        test: () => isEmail(session.authBag.identifier),
        message: usedContent?.ext?.invalid_identifier_error_message ?? defaultMessages.invalid_email
      })
    })

    const nameValidation = object({
      'account-first-name': requireName
        ? string()
            .required(
              usedContent?.ext?.invalid_first_name_error_message ?? defaultMessages.invalid_first
            )
            .matches(
              /^[A-Za-z'\s-]+$/,
              usedContent?.ext?.invalid_first_name_error_message ?? defaultMessages.invalid_first
            )
            .min(
              2,
              usedContent?.ext?.invalid_first_name_error_message ?? defaultMessages.invalid_first
            )
            .max(
              50,
              usedContent?.ext?.invalid_first_name_error_message ?? defaultMessages.invalid_first
            )
        : string().notRequired(),
      'account-last-name': requireName
        ? string()
            .required(
              usedContent?.ext?.invalid_last_name_error_message ?? defaultMessages.invalid_last
            )
            .matches(
              /^[A-Za-z'\s-]+$/,
              usedContent?.ext?.invalid_last_name_error_message ?? defaultMessages.invalid_last
            )
            .min(
              2,
              usedContent?.ext?.invalid_last_name_error_message ?? defaultMessages.invalid_last
            )
            .max(
              50,
              usedContent?.ext?.invalid_last_name_error_message ?? defaultMessages.invalid_last
            )
        : string().notRequired()
    })

    const phoneValidation = object({
      'account-phone': string()
        .required(usedContent?.ext?.invalid_phone_error_message ?? defaultMessages.invalid_phone)
        .transform((x) => x.replace(/\D/g, ''))
        .matches(
          /^[2-9]\d{9}$/,
          usedContent?.ext?.invalid_phone_error_message ?? defaultMessages.invalid_phone
        )
    })

    // two-step push-to-save
    const signUpStep1Validation = nameValidation.concat(signInValidation)

    const signUpValidation = nameValidation.concat(phoneValidation).concat(signInValidation)

    const otpValidation = object({
      'account-code': string().required('Please enter your code').length(4, 'Codes are 4 digits')
    })

    return {
      signInValidation,
      signUpValidation,
      signUpStep1Validation,
      phoneValidation,
      otpValidation
    }
  }
}
